import { useState, useEffect, useMemo, useContext } from 'react';
import { equipmentMaintenanceApi } from '../../services/equipmentMaintenanceService';
import { Plus, Pencil, Trash2, Search, Filter, List, LayoutGrid } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import dayjs from 'dayjs';
import CreateEquipmentMaintenanceModal from '../../components/modals/CreateEquipmentMaintenanceModal';
import EditEquipmentMaintenanceModal from '../../components/modals/EditEquipmentMaintenanceModal';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';
import { AuthContext } from '../../context/AuthContext'; // Adjust path as needed

interface EquipmentMaintenanceRecord {
  id: number;
  date: string;
  store: string;
  store_name?: string;
  rgm: string;
  area_coach: string;
  regional_coach: string;
  description: string;
  category_name?: string;
  subcategory_name?: string;
  category_id: number | null;
  subcategory_id: number | null;
  serial_number: string | null;
  quantity: number;
  group_id: number | null;
  created_at: string;
  updated_at: string;
  created_by: number | null;
}

export interface CreateEquipmentMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  stores: { number: string; name: string }[];
  descriptions: string[];
  rgms: string[];
  areaCoaches: string[];
  regionalCoaches: string[];
}

export interface EditEquipmentMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: number, data: any) => void;
  record: EquipmentMaintenanceRecord | null;
  stores: { number: string; name: string }[];
  descriptions: string[];
  rgms: string[];
  areaCoaches: string[];
  regionalCoaches: string[];
}

export default function EquipmentMaintenance() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin'; // Adjust based on your actual role name
  
  const [records, setRecords] = useState<EquipmentMaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [storeFilter, setStoreFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('card');
  const itemsPerPage = 10;
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<EquipmentMaintenanceRecord | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<EquipmentMaintenanceRecord | null>(null);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      console.log('Fetching equipment maintenance records...');
      const response = await equipmentMaintenanceApi.getAll();
      console.log('Response:', response);
      setRecords(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch equipment maintenance records:', error);
      showErrorToast('Failed to fetch equipment maintenance records');
      setError('Failed to fetch equipment maintenance records');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  // Now the uniqueStores will work correctly
  const uniqueStores = useMemo(() => {
    // Track unique store numbers first
    const storeNumbers = new Set<string>();
    const storeMap = new Map<string, string>(); // Use a map to store number to name

    records.forEach(record => {
      if (record.store) {
        storeNumbers.add(record.store);
        if (record.store_name && !storeMap.has(record.store)) {
            storeMap.set(record.store, record.store_name);
        }
      }
    });
    
    // Convert to an array of objects with both number and name
    return Array.from(storeNumbers).map(storeNumber => ({
      number: storeNumber,
      name: storeMap.get(storeNumber) || storeNumber // Fallback to storeNumber if name not found
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [records]);

  // Add these two useMemo hooks after your uniqueDescriptions useMemo
  const uniqueRgms = useMemo(() => {
    // Extract unique RGM values from records
    const rgmSet = new Set<string>();
    records.forEach(record => {
      if (record.rgm) {
        rgmSet.add(record.rgm);
      }
    });
    return Array.from(rgmSet).sort();
  }, [records]);

  const uniqueAreaCoaches = useMemo(() => {
    // Extract unique Area Coach values from records
    const coachSet = new Set<string>();
    records.forEach(record => {
      if (record.area_coach) {
        coachSet.add(record.area_coach);
      }
    });
    return Array.from(coachSet).sort();
  }, [records]);

  const uniqueRegionalCoaches = useMemo(() => {
    // Extract unique Regional Coach values from records
    const coachSet = new Set<string>();
    records.forEach(record => {
      if (record.regional_coach) {
        coachSet.add(record.regional_coach);
      }
    });
    return Array.from(coachSet).sort();
  }, [records]);

  // Compute unique subcategories for filter dropdown
  const uniqueSubcategories = useMemo(() => {
    const subcatSet = new Set<string>();
    const subcatList: string[] = [];
    records.forEach(record => {
      if (record.subcategory_name) {
        if (!subcatSet.has(record.subcategory_name)) {
          subcatSet.add(record.subcategory_name);
          subcatList.push(record.subcategory_name);
        }
      }
    });
    return subcatList.sort();
  }, [records]);

  // Compute unique descriptions for dropdown
  const uniqueDescriptions = useMemo(() => {
    const descriptionSet = new Set<string>();
    records.forEach(record => {
      if (record.description) {
        descriptionSet.add(record.description);
      }
    });
    return Array.from(descriptionSet).sort();
  }, [records]);

  const handleEdit = (record: EquipmentMaintenanceRecord) => {
    setSelectedRecord(record);
    setIsEditModalOpen(true);
  };

  const handleDelete = (record: EquipmentMaintenanceRecord) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;

    try {
      await equipmentMaintenanceApi.delete(recordToDelete.id);
      await fetchRecords();
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
      showSuccessToast('Equipment maintenance record deleted successfully');
    } catch (error) {
      showErrorToast('Failed to delete equipment maintenance record');
      setError('Failed to delete equipment maintenance record');
    }
  };

  const handleCreateRecord = async (data: any) => {
    try {
      await equipmentMaintenanceApi.create(data);
      await fetchRecords();
      setIsCreateModalOpen(false);
      showSuccessToast('Equipment maintenance record created successfully');
    } catch (error) {
      showErrorToast('Failed to create equipment maintenance record');
      setError('Failed to create equipment maintenance record');
    }
  };

  const handleUpdateRecord = async (id: number, data: any) => {
    try {
      await equipmentMaintenanceApi.update(id, data);
      await fetchRecords();
      setIsEditModalOpen(false);
      showSuccessToast('Equipment maintenance record updated successfully');
    } catch (error) {
      showErrorToast('Failed to update equipment maintenance record');
      setError('Failed to update equipment maintenance record');
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, storeFilter, subcategoryFilter]);

  // Filter records based on search term and store/subcategory filter
  const filteredRecords = records.filter(record => {
    const searchTermLower = searchTerm.toLowerCase();
    const matchesSearch = 
      (record.store_name?.toLowerCase() || record.store.toLowerCase()).includes(searchTermLower) ||
      record.description.toLowerCase().includes(searchTermLower) ||
      (record.serial_number && record.serial_number.toLowerCase().includes(searchTermLower)) ||
      (record.category_name && record.category_name.toLowerCase().includes(searchTermLower)) ||
      (record.subcategory_name && record.subcategory_name.toLowerCase().includes(searchTermLower));
    const matchesStore = storeFilter === 'all' || record.store === storeFilter;
    const matchesSubcategory = subcategoryFilter === 'all' || record.subcategory_name === subcategoryFilter;
    return matchesSearch && matchesStore && matchesSubcategory;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ADD LOGS HERE
  console.log('--- DEBUG RENDER CYCLE ---');
  console.log('Loading state:', loading);
  console.log('Error state:', error);
  console.log('Total records fetched:', records.length);
  console.log('Search term:', searchTerm);
  console.log('Store filter:', storeFilter);
  console.log('Subcategory filter:', subcategoryFilter);
  console.log('Filtered records count:', filteredRecords.length);
  console.log('Paginated records count:', paginatedRecords.length);
  console.log('Current page:', currentPage, 'Total pages:', totalPages);

  // Pagination controls with fewer page buttons for mobile
  const PaginationControls = () => {
    // Limited pagination display for better mobile experience
    const pageNumbers = [];
    
    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include first page
      pageNumbers.push(1);
      
      // Show pages around current page
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pageNumbers.push(i);
      }
      
      // Always include last page
      if (currentPage < totalPages - 2) {
        if (currentPage < totalPages - 3) {
          pageNumbers.push(-1); // Represents ellipsis
        }
        pageNumbers.push(totalPages);
      }
    }
    
    return (
      <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Previous
          </button>
          <span className="mx-2 text-sm text-gray-700 dark:text-gray-300">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{filteredRecords.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, filteredRecords.length)}
              </span>{' '}
              of <span className="font-medium">{filteredRecords.length}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
              >
                Previous
              </button>
              {pageNumbers.map((pageNum, index) => (
                pageNum === -1 ? (
                  <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600">
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === pageNum
                      ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                      : 'text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0'
                      }`}
                  >
                    {pageNum}
                  </button>
                )
              ))}
              <button
                onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // Mobile view for record card
  const MobileRecordCard = ({ record }: { record: EquipmentMaintenanceRecord }) => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{record.store_name || record.store}</h3>
          <div className="text-sm text-gray-600 dark:text-gray-400">{dayjs(record.date).format('YYYY-MM-DD')}</div>
        </div>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => handleEdit(record)}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 p-1 rounded-md hover:bg-indigo-100 dark:hover:bg-gray-700"
            title="Edit Record"
          >
            <Pencil size={18} />
          </button>
          {isAdmin && (
            <button
              onClick={() => handleDelete(record)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-100 dark:hover:bg-gray-700"
              title="Delete Record"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>
      <p className="text-md text-gray-700 dark:text-gray-300 mb-1"><strong className="font-semibold text-gray-800 dark:text-gray-100"> {record.description}</strong></p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong className="font-semibold text-gray-800 dark:text-gray-100">Category:</strong> {record.category_name || 'N/A'}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong className="font-semibold text-gray-800 dark:text-gray-100">Subcategory:</strong> {record.subcategory_name || 'N/A'}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong className="font-semibold text-gray-800 dark:text-gray-100">Serial No:</strong> {record.serial_number || 'N/A'}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong className="font-semibold text-gray-800 dark:text-gray-100">Quantity:</strong> {record.quantity}</p>
    </div>
  );

  // Add these logs to check what's happening
  useEffect(() => {
    if (records.length > 0) {
      console.log('=== DEBUG: First few records and their store names ===');
      records.slice(0, 5).forEach(record => {
        console.log(`Record ID ${record.id}, Store: ${record.store}, Found name: ${record.store_name || record.store}`);
      });
    }
  }, [records]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-red-500 dark:text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 pb-6">
      <div className="mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Equipment Maintenance</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage equipment maintenance records</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 self-start sm:self-auto"
        >
          <Plus size={18} />
          <span className="text-sm sm:text-base">Add Record</span>
        </button>
      </div>

      {/* Search and filters */}
      <div className="mb-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search records..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg flex items-center gap-1 text-sm"
          >
            <Filter size={16} />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {/* View Mode Toggle Buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 ${viewMode === 'table' ? 'bg-gray-200 dark:bg-gray-600 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'}`}
              title="Table View"
            >
              <List size={18} />
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 ${viewMode === 'card' ? 'bg-gray-200 dark:bg-gray-600 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'}`}
              title="Card View"
            >
              <LayoutGrid size={18} />
            </button>
          </div>
        </div>

        {/* Filter options */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Store
                </label>
                <select
                  value={storeFilter}
                  onChange={(e) => setStoreFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="all">All Stores</option>
                  {uniqueStores.map(store => (
                    <option key={store.number} value={store.number}>{store.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sub Category
                </label>
                <select
                  value={subcategoryFilter}
                  onChange={(e) => setSubcategoryFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="all">All Sub Categories</option>
                  {uniqueSubcategories.map(subcat => (
                    <option key={subcat} value={subcat}>{subcat}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="mb-3 text-sm text-gray-500 dark:text-gray-400">
        Found {filteredRecords.length} records
        {storeFilter !== 'all' && ` for ${uniqueStores.find(s => s.number === storeFilter)?.name || storeFilter}`}
      </div>

      {/* Main content - Table for desktop, cards for mobile */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
        {/* The redundant table and card rendering logic that was here has been removed. */}

        {/* Refined Rendering Logic: */}
        {viewMode === 'table' ? (
          <div className="overflow-x-auto hidden sm:block"> {/* Table view shown on medium screens and up */}
             <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                   {/* ... table head ... */}
                   <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Store
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Serial Number
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Quantity
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                        </th>
                    </tr>
                    </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{record.id}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                            {dayjs(record.date).format('YYYY-MM-DD')}
                            </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{record.store_name || record.store}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap max-w-[200px] truncate">
                            <div className="text-sm text-gray-900 dark:text-white">{record.description}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                            {record.category_name || '-'}
                            {record.category_name && record.subcategory_name ? ` / ${record.subcategory_name}` : record.subcategory_name || ''}
                            </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{record.serial_number || '-'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{record.quantity}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <button
                            onClick={() => handleEdit(record)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                            >
                            <Pencil size={16} />
                            </button>
                            {/* Delete button removed as per requirements */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          </div>
        ) : (
          <div className="sm:block"> {/* Card view shown on all screen sizes if selected, or on small screens by default */}
            {paginatedRecords.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedRecords.map((record) => (
                  <MobileRecordCard key={record.id} record={record} />
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                No records found
              </div>
            )}
          </div>
        )}
        
        <PaginationControls />
      </div>

      <CreateEquipmentMaintenanceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateRecord}
        stores={uniqueStores}
        descriptions={uniqueDescriptions}
        rgms={uniqueRgms}
        areaCoaches={uniqueAreaCoaches}
        regionalCoaches={uniqueRegionalCoaches}
      />

      <EditEquipmentMaintenanceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdateRecord}
        record={selectedRecord}
        stores={uniqueStores}
        descriptions={uniqueDescriptions}
        rgms={uniqueRgms}
        areaCoaches={uniqueAreaCoaches}
        regionalCoaches={uniqueRegionalCoaches}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemType="Equipment Maintenance Record"
        itemName={recordToDelete?.description || ''}
      />
    </div>
  );
}